"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Linkedin, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConsent } from "@/lib/consent";
import { useTenant } from "@/lib/tenant";
import { FOOTER_COLUMNS, FOOTER_LEGAL_LINKS } from "@/lib/navigation";
import { routes } from "@/lib/routes";
import { LEGAL_ENTITY, LINKEDIN_PAGES } from "@landvest/legal-entity";
import { track } from "@/lib/analytics";
import { LandvestHandoffLink } from "@/components/layout/LandvestHandoffLink";

/**
 * Outbound link to landvest.eu, UTM-tagged so the visit is attributable on
 * the LandVest side (its analyses persist `utm_*`) and tracked here as a
 * PostHog event so the click is attributable on this side.
 */
const LANDVEST_FOOTER_URL = "https://landvest.eu/?utm_source=oekopunktemarkt.de&utm_medium=footer&utm_campaign=opm-footer-brand";

/** Desktop grid for the number of FOOTER_COLUMNS (static class names for Tailwind). */
const COLUMN_GRID: Record<number, string> = {
  4: "grid-cols-4",
  5: "grid-cols-5"
};

/**
 * Site footer: brand row, one column per FOOTER_COLUMNS entry, bottom bar.
 * Follows the Stitch redesign; mobile shows the same columns as an accordion.
 *
 * Pre-footer CTAs are placed intentionally per layout/page
 * (e.g. CTABandSection in content layouts, FinalCTASection on homepage).
 */
export function PageFooter() {
  const $ = _c(81);
  const {
    openSettings,
    hasConsented
  } = useConsent();
  const tenant = useTenant();
  const [openSection, setOpenSection] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = id => {
      setOpenSection(prev => prev === id ? null : id);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const toggleSection = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== openSection || $[2] !== tenant.logo.image || $[3] !== tenant.logo.text || $[4] !== tenant.logo.textSuffix || $[5] !== tenant.name) {
    const footerAccordionSections = FOOTER_COLUMNS.map(_temp);
    t7 = "page-footer";
    t8 = "mt-auto";
    t6 = "bg-primary-deep text-surface w-full pt-20 pb-12 px-6 md:px-16 lg:px-24";
    t5 = "max-w-[1440px] mx-auto";
    t3 = "lg:hidden mb-12";
    let t9;
    if ($[14] !== tenant.logo.image || $[15] !== tenant.name) {
      t9 = tenant.logo.image ? <Image src={tenant.logo.image} alt={tenant.name} width={32} height={32} className="h-8 w-8" /> : null;
      $[14] = tenant.logo.image;
      $[15] = tenant.name;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== tenant.logo.textSuffix) {
      t10 = tenant.logo.textSuffix && <span className="text-neon">{tenant.logo.textSuffix}</span>;
      $[17] = tenant.logo.textSuffix;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== t10 || $[20] !== tenant.logo.text) {
      t11 = <span className="font-heading font-bold text-surface text-lg">{tenant.logo.text}{t10}</span>;
      $[19] = t10;
      $[20] = tenant.logo.text;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t11 || $[23] !== t9) {
      t12 = <Link href={routes.home} className="flex items-center gap-2">{t9}{t11}</Link>;
      $[22] = t11;
      $[23] = t9;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    let t14;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <p className="mt-2 text-surface/60 text-xs leading-relaxed">Der transparente Marktplatz für naturschutzrechtliche Kompensation.</p>;
      t14 = <LandVestAttribution placement="mobile" className="mt-5" />;
      $[25] = t13;
      $[26] = t14;
    } else {
      t13 = $[25];
      t14 = $[26];
    }
    if ($[27] !== t12) {
      t4 = <div className="pb-8 border-b border-white/5">{t12}{t13}{t14}</div>;
      $[27] = t12;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    t1 = "divide-y divide-white/5";
    let t15;
    if ($[29] !== openSection) {
      t15 = (section, i) => <div key={section.id}><button onClick={() => toggleSection(section.id)} className={cn("w-full px-0 py-5 flex items-center justify-between", i === 0 && openSection !== section.id ? "text-neon" : "text-surface/70")} aria-expanded={openSection === section.id}><span className="text-[10px] tracking-widest uppercase font-medium">{section.heading}</span><ChevronDown className={cn("w-4 h-4 transition-transform", openSection === section.id && "rotate-180")} /></button>{openSection === section.id && <ul className="pb-4 space-y-3 pl-1">{section.links.map(_temp2)}</ul>}</div>;
      $[29] = openSection;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    t2 = footerAccordionSections.map(t15);
    $[1] = openSection;
    $[2] = tenant.logo.image;
    $[3] = tenant.logo.text;
    $[4] = tenant.logo.textSuffix;
    $[5] = tenant.name;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[31] !== t1 || $[32] !== t2) {
    t9 = <div className={t1}>{t2}</div>;
    $[31] = t1;
    $[32] = t2;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="pt-8 flex flex-col items-center gap-6"><div className="flex items-center gap-4"><a href={LINKEDIN_PAGES.oekopunktemarkt} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full glass-dark text-neon hover:bg-neon hover:text-surface transition-all duration-300" aria-label="LinkedIn"><Linkedin className="w-5 h-5" /></a></div></div>;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t3 || $[36] !== t4 || $[37] !== t9) {
    t11 = <div className={t3}>{t4}{t9}{t10}</div>;
    $[35] = t3;
    $[36] = t4;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== tenant.logo.image || $[40] !== tenant.name) {
    t12 = tenant.logo.image ? <Image src={tenant.logo.image} alt={tenant.name} width={32} height={32} className="h-8 w-8" /> : null;
    $[39] = tenant.logo.image;
    $[40] = tenant.name;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== tenant.logo.textSuffix) {
    t13 = tenant.logo.textSuffix && <span className="text-neon">{tenant.logo.textSuffix}</span>;
    $[42] = tenant.logo.textSuffix;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== tenant.logo.text) {
    t14 = <span className="font-heading font-bold text-surface text-lg">{tenant.logo.text}{t13}</span>;
    $[44] = t13;
    $[45] = tenant.logo.text;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t12 || $[48] !== t14) {
    t15 = <Link href={routes.home} className="flex items-center gap-2">{t12}{t14}</Link>;
    $[47] = t12;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="text-surface/70 text-sm leading-relaxed">Der transparente Marktplatz für naturschutzrechtliche Kompensation in Deutschland.</p>;
    t17 = <LandVestAttribution placement="desktop" />;
    $[50] = t16;
    $[51] = t17;
  } else {
    t16 = $[50];
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t15) {
    t18 = <div className="max-w-md space-y-6">{t15}{t16}{t17}</div>;
    $[52] = t15;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="flex items-center gap-3"><a href={LINKEDIN_PAGES.oekopunktemarkt} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full glass-dark text-neon hover:bg-neon hover:text-surface transition-all duration-300" aria-label="LinkedIn"><Linkedin className="w-5 h-5" /></a></div>;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t18) {
    t20 = <div className="flex items-start justify-between gap-12 pb-12 mb-12 border-b border-white/10">{t18}{t19}</div>;
    $[55] = t18;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className={cn("grid gap-8", COLUMN_GRID[FOOTER_COLUMNS.length] ?? "grid-cols-5")}>{FOOTER_COLUMNS.map(_temp4)}</div>;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t20) {
    t22 = <div className="hidden lg:block mb-20">{t20}{t21}</div>;
    $[58] = t20;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="text-surface/50 text-sm">© {new Date().getFullYear()} {LEGAL_ENTITY.name}</span>;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = FOOTER_LEGAL_LINKS.map(_temp5);
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== hasConsented || $[63] !== openSettings) {
    t25 = hasConsented && <><span className="text-white/10">|</span><button onClick={openSettings} className="text-surface/50 hover:text-neon transition-colors cursor-pointer">Cookie-Einstellungen</button></>;
    $[62] = hasConsented;
    $[63] = openSettings;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  let t27;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="text-white/10">|</span>;
    t27 = <Link href={routes.accessRecovery} className="text-surface/50 hover:text-neon transition-colors">Gastzugang wiederherstellen</Link>;
    $[65] = t26;
    $[66] = t27;
  } else {
    t26 = $[65];
    t27 = $[66];
  }
  let t28;
  if ($[67] !== t25) {
    t28 = <div className="pt-12 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">{t23}<nav className="flex flex-wrap justify-center gap-6 text-sm">{t24}{t25}{t26}{t27}</nav></div>;
    $[67] = t25;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t11 || $[70] !== t22 || $[71] !== t28 || $[72] !== t5) {
    t29 = <div className={t5}>{t11}{t22}{t28}</div>;
    $[69] = t11;
    $[70] = t22;
    $[71] = t28;
    $[72] = t5;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t29 || $[75] !== t6) {
    t30 = <div className={t6}>{t29}</div>;
    $[74] = t29;
    $[75] = t6;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t30 || $[78] !== t7 || $[79] !== t8) {
    t31 = <footer id={t7} className={t8}>{t30}</footer>;
    $[77] = t30;
    $[78] = t7;
    $[79] = t8;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  return t31;
}

/**
 * "Eine Marke der LandVest GmbH" — small LandVest wordmark linking out to
 * landvest.eu. Ökopunktemarkt is operated by LandVest GmbH (see Impressum),
 * so the wording is the standard brand-of-company attribution, not a
 * partnership claim.
 */
function _temp5(link_1, i_0) {
  return <span key={link_1.href} className="contents">{i_0 > 0 && <span className="text-white/10">|</span>}<Link href={link_1.href} className="text-surface/50 hover:text-neon transition-colors">{link_1.label}</Link></span>;
}
function _temp4(column_0) {
  return <nav key={column_0.ariaLabel} aria-label={column_0.ariaLabel}><FooterHeading>{column_0.heading}</FooterHeading><ul className="space-y-3">{column_0.links.map(_temp3)}</ul></nav>;
}
function _temp3(link_0) {
  return <FooterLink key={`${link_0.href}-${link_0.label}`} href={link_0.href} label={link_0.label} />;
}
function _temp2(link) {
  return <FooterLink key={`${link.href}-${link.label}`} href={link.href} label={link.label} />;
}
function _temp(column) {
  return {
    id: column.ariaLabel,
    heading: column.heading,
    links: column.links
  };
}
function LandVestAttribution(t0) {
  const $ = _c(9);
  const {
    placement,
    className
  } = t0;
  let t1;
  if ($[0] !== placement) {
    t1 = () => track.landvestFooterLinkClicked({
      placement
    });
    $[0] = placement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className) {
    t2 = cn("group inline-flex items-center gap-2.5 text-surface/50 hover:text-surface/80 transition-colors", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-xs">Eine Marke der</span>;
    t4 = <Image src="/logos/landvest-logo-light.svg" alt={LEGAL_ENTITY.name} width={942} height={198} className="h-[14px] w-auto translate-y-[1.5px] opacity-60 group-hover:opacity-100 transition-opacity" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2) {
    t5 = <a href={LANDVEST_FOOTER_URL} target="_blank" rel="noopener noreferrer" onClick={t1} className={t2} aria-label={`Eine Marke der ${LEGAL_ENTITY.name} – zu landvest.eu`}>{t3}{t4}</a>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

/** Neon green uppercase section heading for footer columns */
function FooterHeading(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <h4 className="text-sm font-extrabold text-surface mb-5">{children}</h4>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** Reusable footer link — receives pre-localized href */
function FooterLink(t0) {
  const $ = _c(3);
  const {
    href,
    label
  } = t0;
  let t1;
  if ($[0] !== href || $[1] !== label) {
    t1 = <li><LandvestHandoffLink href={href} className="text-surface/70 hover:text-neon transition-colors duration-200 text-sm">{label}</LandvestHandoffLink></li>;
    $[0] = href;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}